import type { AiChatErrorCode } from "@9amhealth/shared";
import type { FC } from "react";
import React, { useSyncExternalStore } from "react";
import { ChatErrorSimulator } from "src/ui/components/LeapChat/state/ChatErrorSimulator";
import {
  DebugBadge,
  DebugButton,
  DebugButtonRow,
  DebugDivider,
  DebugFormGroup,
  DebugKeyValue,
  DebugToggleSwitch
} from "./debug.styles";

const errorCodes: { code: AiChatErrorCode; defaultStatus?: number }[] = [
  { code: "network" },
  { code: "offline" },
  { code: "auth", defaultStatus: 401 },
  { code: "rate_limit", defaultStatus: 429 },
  { code: "server_error", defaultStatus: 500 },
  { code: "client_error", defaultStatus: 400 },
  { code: "no_body" },
  { code: "stream_error" }
];

function getSnapshot() {
  return ChatErrorSimulator.current;
}

const DebugChatErrors: FC = () => {
  const config = useSyncExternalStore(
    // eslint-disable-next-line @typescript-eslint/unbound-method
    ChatErrorSimulator.subscribe,
    getSnapshot
  );
  const [persistent, setPersistent] = React.useState(false);
  const [customStatus, setCustomStatus] = React.useState("");

  const handleSelect = (code: AiChatErrorCode, defaultStatus?: number) => {
    const statusCode = customStatus
      ? parseInt(customStatus, 10) || undefined
      : defaultStatus;
    ChatErrorSimulator.set(code, statusCode, persistent);
  };

  return (
    <div>
      <DebugKeyValue>
        <span className="key">Status</span>
        <span className="value">
          {config ? (
            <DebugBadge variant={config.persistent ? "warning" : "danger"}>
              {config.code}
              {config.statusCode ? ` (${config.statusCode})` : ""}
              {config.persistent ? " · persistent" : " · one-shot"}
            </DebugBadge>
          ) : (
            <DebugBadge variant="info">Inactive</DebugBadge>
          )}
        </span>
      </DebugKeyValue>

      <DebugDivider />

      <DebugFormGroup>
        <label>Error Code</label>
        <DebugButtonRow>
          {errorCodes.map(({ code, defaultStatus }) => (
            <DebugButton
              key={code}
              className={config?.code === code ? "primary small" : "small"}
              onClick={() => handleSelect(code, defaultStatus)}
            >
              {code}
            </DebugButton>
          ))}
        </DebugButtonRow>
      </DebugFormGroup>

      <DebugFormGroup>
        <label>HTTP Status (optional, overrides default)</label>
        <input
          type="number"
          value={customStatus}
          onChange={(e) => setCustomStatus(e.target.value)}
          placeholder="e.g. 500, 401"
          style={{ maxWidth: "120px" }}
        />
      </DebugFormGroup>

      <DebugToggleSwitch>
        <span className="label">Persistent (keep active after each send)</span>
        <input
          type="checkbox"
          checked={persistent}
          onChange={(e) => {
            setPersistent(e.target.checked);
            if (config) {
              ChatErrorSimulator.set(
                config.code,
                config.statusCode,
                e.target.checked
              );
            }
          }}
        />
        <span className="switch" />
      </DebugToggleSwitch>

      <DebugButtonRow style={{ marginTop: "0.75rem" }}>
        <DebugButton
          className="danger"
          onClick={() => {
            ChatErrorSimulator.clear();
            setCustomStatus("");
          }}
        >
          Clear
        </DebugButton>
      </DebugButtonRow>
    </div>
  );
};

export default DebugChatErrors;
